import React, { createContext, useState, useCallback, useContext, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { ToastContext } from './ToastContext';

export const TransactionContext = createContext(null);

export const TransactionProvider = ({ children }) => {
  const { apiFetch } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    dateMode: 'month',
    search: '',
    type: undefined,
    categories: undefined,
    uncategorized: false
  });

  const [groups, setGroups] = useState({
    upcoming: [],
    today: [],
    yesterday: [],
    last7Days: [],
    last14Days: [],
    currentMonth: [],
    history: {}
  });
  const [summary, setSummary] = useState({ balance: 0, projectedBalance: 0 });
  const [allPlannedTransactions, setAllPlannedTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [predictionData, setPredictionData] = useState(null);

  const [currentMonthProjection, setCurrentMonthProjection] = useState(null);

  const [pastMonthBalance, setPastMonthBalance] = useState(null);

  const fetchAbortRef = useRef(null);
  const fetchRequestIdRef = useRef(0);

  const fetchTransactions = useCallback(async (activeFilters) => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);
    try {
      const today = new Date();
      const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      let isFutureMonth = false;
      let isPastMonth = false;
      let targetYear = today.getFullYear();
      let targetMonth = today.getMonth() + 1;

      if (activeFilters.dateMode === 'month' && activeFilters.startDate) {
        const selectedMonthDate = new Date(activeFilters.startDate);
        targetYear = selectedMonthDate.getFullYear();
        targetMonth = selectedMonthDate.getMonth() + 1;
        if (selectedMonthDate >= startOfNextMonth) {
          isFutureMonth = true;
        } else if (selectedMonthDate < startOfCurrentMonth) {
          isPastMonth = true;
        }
      }

      const signal = abortController.signal;

      let mainRequest;
      if (isFutureMonth) {
        mainRequest = apiFetch(`/api/predictions/projection?year=${targetYear}&month=${targetMonth}`, { signal });
      } else {
        const params = new URLSearchParams();
        if (activeFilters.startDate) params.append('startDate', activeFilters.startDate);
        if (activeFilters.endDate) params.append('endDate', activeFilters.endDate);
        if (activeFilters.search) params.append('search', activeFilters.search);
        if (activeFilters.type) params.append('type', activeFilters.type);
        if (activeFilters.categories) params.append('categories', activeFilters.categories);
        if (activeFilters.uncategorized) params.append('uncategorized', activeFilters.uncategorized);
        mainRequest = apiFetch(`/api/transactions?${params.toString()}`, { signal });
      }

      const [mainRes, projectionRes, monthBalanceRes] = await Promise.all([
        mainRequest,
        apiFetch('/api/predictions/projection/current', { signal }),
        isPastMonth
          ? apiFetch(`/api/transactions/balance?year=${targetYear}&month=${targetMonth}`, { signal })
          : Promise.resolve(null)
      ]);

      const mainDto = mainRes.ok ? await mainRes.json() : null;
      const projectionDto = projectionRes.ok ? await projectionRes.json() : null;
      const monthBalanceDto = monthBalanceRes && monthBalanceRes.ok ? await monthBalanceRes.json() : null;

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      setPastMonthBalance(isPastMonth && monthBalanceDto ? monthBalanceDto.balance : null);

      if (isFutureMonth) {
        setPredictionData(mainDto);
        if (mainDto) {
          setGroups({
            upcoming: mainDto.plannedTransactions || [],
            today: [],
            yesterday: [],
            last7Days: [],
            last14Days: [],
            currentMonth: [],
            history: {}
          });

          const totalPlanned = (mainDto.plannedTransactions || []).reduce((sum, t) => sum + t.amount, 0);
          setSummary({
            balance: mainDto.chainedBalance,
            projectedBalance: mainDto.chainedBalance + totalPlanned
          });
        }
      } else {
        setPredictionData(null);
        if (mainDto) {
          setGroups(mainDto.groups);
          setSummary(mainDto.summary);
        }
      }

      if (projectionDto) {
        setCurrentMonthProjection(projectionDto);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching transactions:', err);
      showToast('Einträge konnten nicht geladen werden.');
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiFetch, showToast]);

  const fetchPlannedTransactions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/transactions/planned');
      if (res.ok) {
        setAllPlannedTransactions(await res.json());
      }
    } catch (err) {
      console.error('Error fetching planned transactions:', err);
      showToast('Geplante Einträge konnten nicht geladen werden.');
    }
  }, [apiFetch, showToast]);

  useEffect(() => {
    fetchPlannedTransactions();
  }, [fetchPlannedTransactions]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(newFilters)]);
      for (const key of keys) {
        if (prev[key] !== newFilters[key]) return newFilters;
      }
      return prev;
    });
  }, []);

  const refetch = useCallback(() => {
    fetchTransactions(filters);
    fetchPlannedTransactions();
  }, [fetchTransactions, filters, fetchPlannedTransactions]);

  const value = {
    filters,
    updateFilters,
    groups,
    summary,
    allPlannedTransactions,
    predictionData,
    currentMonthProjection,
    pastMonthBalance,
    loading,
    fetchTransactions,
    refetch
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};
